import type { UserSummary } from '@/lib/api/generated/model';
import { cn } from '@/lib/utils';

import { displayNameOf } from './accountIdentity';
import { UserAvatar } from './UserAvatar';

/**
 * A person beside other facts on a line: their disc and their name, the
 * address on hover (spec 158).
 *
 * The disc is decorative because the name is right beside it; the address goes
 * on the whole chip, since two colleagues sharing a display name is exactly
 * what the name alone cannot tell apart. Callers render nothing for "nobody"
 * rather than passing a placeholder: what "nobody" means differs by surface.
 *
 * The chip sits on the line by its name (spec 177). Centred, its baseline came
 * from the disc's initials, a smaller face, and the name sat a pixel below the
 * line's other facts; the disc now centres itself and takes no part in it.
 */
export function PersonName({
  person,
  className,
}: {
  person: UserSummary;
  className?: string;
}) {
  const name = displayNameOf(person.display_name, person.email);
  return (
    <span
      title={`${name} · ${person.email}`}
      className={cn('inline-flex min-w-0 items-baseline gap-1', className)}
    >
      <UserAvatar
        id={person.id}
        displayName={person.display_name}
        email={person.email}
        className="self-center"
      />
      <span className="text-foreground max-w-40 truncate">{name}</span>
    </span>
  );
}
